import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import UploadImage from "./UploadImage";
import { useRecoilValue } from "recoil";
import { transactionClaimAtom } from "../state/filter";
import { API_BASE_URL } from "../config/api";

type ServiceData = Record<string, { id: string; label: string }[]>;

const serviceData: ServiceData = {
    Sneakers: [
        { id: "sneaker-de-yellowing", label: "De-Yellowing" },
        { id: "sneaker-stain-removal", label: "Stain Removal" },
        { id: "sneaker-scuff-and-scratch-refinement", label: "Scuff and Scratch Refinement" },
        { id: "sneaker-re-stitching", label: "Re-Stitching" },
        { id: "sneaker-edge-restoration", label: "Edge Restoration" },
        { id: "sneaker-crease-reduction", label: "Crease Reduction" },
    ],
    Handbags: [
        { id: "handbag-stain-removal", label: "Stain Removal" },
        { id: "handbag-repair-and-stitching", label: "Repair & Stitching" },
        { id: "handbag-zip-repair", label: "Zip Repair" },
        { id: "handbag-edge-restoration", label: "Edge Restoration" },
        { id: "handbag-bag-piping-touch-up", label: "Bag Piping Touch-Up" },
        { id: "handbag-strap-color-restoration", label: "Strap Color Restoration" },
        { id: "handbag-shape-restoration", label: "Shape Restoration" },
    ],
    "Formal Shoes": [
        { id: "formal-sole-repair", label: "Sole Repair" },
        { id: "formal-mould-repair-and-treatment", label: "Mould Repair and Treatment" },
        { id: "formal-stain-removal", label: "Stain Removal (Spot Treatment)" },
        { id: "formal-edge-dressing-and-welt-coating", label: "Edge Dressing and Welt Coating" },
        { id: "formal-crease-reduction", label: "Crease Reduction (Toe-Box)" },
        { id: "formal-gloss-or-matte-finish-restoration", label: "Gloss or Matte Finish Restoration" },
        { id: "formal-suede-tone-equalization", label: "Suede Tone Equalization" },
    ],
};

const ZEESH_MEMBER_UUIDS = new Set(["78ddf74f-b2b6-4774-b94a-5c0ef44f569d"]);
const CONVERSE_MEMBER_UUIDS = new Set([
    "2ef6bbd7-90f1-4889-aff5-d7a95347064c",
    "e86fcea5-b5bc-43e5-9286-9f05eceed7f2",
    "656f9a98-a100-494d-ad90-ef4f6f0e1020",
]);
const BEHNO_MEMBER_UUIDS = new Set([
    "79b28b5a-7482-42df-9340-cf2c562211ae",
    "ba7e99cd-bed4-4f54-968c-7a5e36c7e185",
    "30407374-2aa8-458f-a27e-4eedb2bca0ef",
]);
const OROH_MEMBER_UUIDS = new Set(["b54629ec-16bd-40ce-8bf7-47cd8ca59d1d"]);

const sanitizeClaimText = (str: string): string => {
    if (!str) return str;
    return str
        .replace(
            /This item has been exchanged\. Please Request Service on your replacement sale ID\./gi,
            "💡This item has been exchanged. Please submit a Service Request on your replacement product.",
        )
        .replace(/replacement sale ID/gi, "replacement product")
        .replace(/\bRequest Services\b/gi, "Service Requests")
        .replace(/\brequest services\b/gi, "Service Requests")
        .replace(/\bRequest Service\b/gi, "Service Request")
        .replace(/\brequest service\b/gi, "Service Request")
        .replace(/\bClaims\b/g, "Service Requests")
        .replace(/\bclaims\b/g, "Service Requests")
        .replace(/\bClaim\b/g, "Service Request")
        .replace(/\bclaim\b/g, "Service Request");
};

type ClaimStatus =
    | "REQUESTED"
    | "ACCEPTED_PICKUP"
    | "PRE_QC"
    | "IN_SERVICE"
    | "POST_QC"
    | "DISPATCHED"
    | "DELIVERED"
    | "REJECTED"
    | "CANCELLED";

type LatestClaimInfo = {
    id: number;
    claimStatus: ClaimStatus;
};

const IN_REVIEW_STATUSES: ClaimStatus[] = ["REQUESTED", "ACCEPTED_PICKUP"];
const IN_CARE_STATUSES: ClaimStatus[] = ["PRE_QC", "IN_SERVICE", "POST_QC", "DISPATCHED"];

interface ApiError {
    type:
        | "expired"
        | "limit_reached"
        | "gap_restriction"
        | "invalid_booking"
        | "validation"
        | "network"
        | "unknown"
        | "otp";
    message: string;
    nextClaimDate?: Date | null;
}

interface EntitlementProduct {
    saleId: number;
    productName: string;
    product: string | null;
    color: string | null;
    size: number | null;
    brand: string | null;
    status: "created" | "exchanged";
    uniqueId: string;
    transactionDate: string | null;
    scheduleUrl: string | null;
    isReplacement: boolean;
    hasActiveClaim: boolean;
}

interface EntitlementData {
    entitlementId: string;
    memberUuid: string;
    customerName: string | null;
    phoneNo: string | null;
    rootUniqueId: string;
    planCode: string;
    status: string;
    maxClaims: number;
    usedClaims: number;
    remainingClaims: number;
    globalCooldownActive?: boolean;
    nextClaimDate?: string | null;
    expiresAt: string | null;
    products: EntitlementProduct[];
}

const isEntitlementId = (id: string | undefined): boolean => {
    if (!id) return false;
    return isNaN(Number(id));
};

const ErrorIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);
const ClockIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);
const BanIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
    </svg>
);
const CalendarIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const getFileStatus = (fileUrl: string, statuses: Record<string, string>): string | undefined => {
    if (!statuses || !fileUrl) return undefined;
    if (statuses[fileUrl]) return statuses[fileUrl];

    const rawFname = fileUrl.split("/").pop() || fileUrl;
    if (statuses[rawFname]) return statuses[rawFname];

    const cleanFname = rawFname.split("?")[0];
    if (statuses[cleanFname]) return statuses[cleanFname];

    try {
        const decodedFname = decodeURIComponent(cleanFname.replace(/\+/g, " "));
        if (statuses[decodedFname]) return statuses[decodedFname];
    } catch {
        // fallback
    }

    const matchedKey = Object.keys(statuses).find((key) => {
        try {
            const cleanKey = key.split("?")[0];
            const decodedKey = decodeURIComponent(cleanKey.replace(/\+/g, " "));
            const decodedFname = decodeURIComponent(cleanFname.replace(/\+/g, " "));
            return cleanKey === cleanFname || decodedKey === decodedFname || cleanKey.endsWith(cleanFname) || cleanFname.endsWith(cleanKey);
        } catch {
            return false;
        }
    });

    return matchedKey ? statuses[matchedKey] : undefined;
};

const parseApiError = (errorResponse: any): ApiError => {
    const rawMessage = errorResponse?.message || errorResponse?.error || "Something went wrong";
    const message = sanitizeClaimText(rawMessage);
    const lowerMessage = rawMessage.toLowerCase();

    if (lowerMessage.includes("claim period expired") || lowerMessage.includes("subscription expired")) {
        return {
            type: "expired",
            message: "Your Service Request period has expired. A Service Request must be submitted within the validity period of your purchase.",
        };
    }
    if (lowerMessage.includes("all claims are over") || lowerMessage.includes("claim limit")) {
        return { type: "limit_reached", message };
    }
    if (lowerMessage.includes("next claim allowed after")) {
        const dateMatch = rawMessage.match(/after\s+(.+)/i);
        let nextClaimDate: Date | null = null;
        if (dateMatch && dateMatch[1]) nextClaimDate = new Date(dateMatch[1]);
        return {
            type: "gap_restriction",
            message: "You need to wait before submitting another Service Request.",
            nextClaimDate: nextClaimDate && !isNaN(nextClaimDate.getTime()) ? nextClaimDate : null,
        };
    }
    if (lowerMessage.includes("invalid booking") || lowerMessage.includes("bookingref is required")) {
        return {
            type: "invalid_booking",
            message: "The booking reference is invalid or not found. Please check your Care+ ID.",
        };
    }
    if (lowerMessage.includes("invalid pickupdate format")) {
        return { type: "validation", message: "Invalid pickup date format. Please select a valid date." };
    }
    if (lowerMessage.includes("required") || lowerMessage.includes("invalid")) {
        return { type: "validation", message };
    }
    return { type: "unknown", message };
};

interface ErrorModalProps {
    error: ApiError;
    onClose: () => void;
}

const ErrorModal: React.FC<ErrorModalProps> = ({ error, onClose }) => {
    const getErrorConfig = () => {
        switch (error.type) {
            case "expired":
                return {
                    icon: <ClockIcon />,
                    title: "Service Request Period Expired",
                    bgColor: "bg-orange-50",
                    borderColor: "border-orange-200",
                    iconBg: "bg-orange-100",
                    iconColor: "text-orange-600",
                    titleColor: "text-orange-800",
                    messageColor: "text-orange-700",
                };
            case "limit_reached":
                return {
                    icon: <BanIcon />,
                    title: "Service Request Limit Reached",
                    bgColor: "bg-red-50",
                    borderColor: "border-red-200",
                    iconBg: "bg-red-100",
                    iconColor: "text-red-600",
                    titleColor: "text-red-800",
                    messageColor: "text-red-700",
                };
            case "gap_restriction":
                return {
                    icon: <CalendarIcon />,
                    title: "Service Request Not Yet Available",
                    bgColor: "bg-blue-50",
                    borderColor: "border-blue-200",
                    iconBg: "bg-blue-100",
                    iconColor: "text-blue-600",
                    titleColor: "text-blue-800",
                    messageColor: "text-blue-700",
                };
            case "invalid_booking":
                return {
                    icon: <ErrorIcon />,
                    title: "Invalid Booking",
                    bgColor: "bg-gray-50",
                    borderColor: "border-gray-200",
                    iconBg: "bg-gray-100",
                    iconColor: "text-gray-600",
                    titleColor: "text-gray-800",
                    messageColor: "text-gray-700",
                };
            case "otp":
                return {
                    icon: <ErrorIcon />,
                    title: "OTP Error",
                    bgColor: "bg-yellow-50",
                    borderColor: "border-yellow-200",
                    iconBg: "bg-yellow-100",
                    iconColor: "text-yellow-600",
                    titleColor: "text-yellow-800",
                    messageColor: "text-yellow-700",
                };
            case "validation":
                return {
                    icon: <ErrorIcon />,
                    title: "Missing Information",
                    bgColor: "bg-yellow-50",
                    borderColor: "border-yellow-200",
                    iconBg: "bg-yellow-100",
                    iconColor: "text-yellow-600",
                    titleColor: "text-yellow-800",
                    messageColor: "text-yellow-700",
                };
            case "network":
                return {
                    icon: <ErrorIcon />,
                    title: "Connection Error",
                    bgColor: "bg-purple-50",
                    borderColor: "border-purple-200",
                    iconBg: "bg-purple-100",
                    iconColor: "text-purple-600",
                    titleColor: "text-purple-800",
                    messageColor: "text-purple-700",
                };
            default:
                return {
                    icon: <ErrorIcon />,
                    title: "Error",
                    bgColor: "bg-red-50",
                    borderColor: "border-red-200",
                    iconBg: "bg-red-100",
                    iconColor: "text-red-600",
                    titleColor: "text-red-800",
                    messageColor: "text-red-700",
                };
        }
    };
    const config = getErrorConfig();
    const formatDate = (date: Date) =>
        date.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4"
            onClick={onClose}
        >
            <div
                className={`${config.bgColor} ${config.borderColor} border rounded-xl shadow-2xl max-w-md w-full p-4 sm:p-6`}
                onClick={(e) => e.stopPropagation()}
                style={{ animation: "fadeIn 0.2s ease-out" }}
            >
                <div className="flex justify-center mb-4">
                    <div className={`${config.iconBg} ${config.iconColor} p-3 sm:p-4 rounded-full`}>{config.icon}</div>
                </div>
                <h3 className={`${config.titleColor} text-lg sm:text-xl font-bold text-center mb-3`}>{config.title}</h3>
                <p className={`${config.messageColor} text-sm sm:text-base text-center mb-4 break-words`}>{error.message}</p>

                {error.type === "gap_restriction" && error.nextClaimDate && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 mb-4 border border-blue-200">
                        <p className="text-xs sm:text-sm text-gray-600 text-center mb-1">
                            You can submit your next Service Request on:
                        </p>
                        <p className="text-base sm:text-lg font-semibold text-blue-700 text-center">
                            {formatDate(error.nextClaimDate)}
                        </p>
                    </div>
                )}
                {error.type === "expired" && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 mb-4 border border-orange-200">
                        <p className="text-xs sm:text-sm text-gray-600 text-center">
                            💡 Service Requests are valid for a specific period after purchase. Please contact support
                            if you believe this is an error.
                        </p>
                    </div>
                )}
                {error.type === "limit_reached" && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 mb-4 border border-red-200">
                        <p className="text-xs sm:text-sm text-gray-600 text-center">
                            💡 Each Care+ subscription includes a limited number of Service Requests. You have used all
                            available Service Requests.
                        </p>
                    </div>
                )}
                {error.type === "validation" && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 mb-4 border border-yellow-200">
                        <p className="text-xs sm:text-sm text-gray-600 text-center">
                            💡 Please fill in all required fields and upload all required photos before submitting.
                        </p>
                    </div>
                )}
                {error.type === "network" && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 mb-4 border border-purple-200">
                        <p className="text-xs sm:text-sm text-gray-600 text-center">
                            💡 Please check your internet connection and try again.
                        </p>
                    </div>
                )}

                <button
                    type="button"
                    onClick={onClose}
                    className="w-full bg-gray-800 hover:bg-gray-900 text-white font-medium py-3 px-4 rounded-lg transition-colors text-sm sm:text-base"
                >
                    Understood
                </button>
            </div>
        </div>
    );
};

interface EligibilityBlockProps {
    error: ApiError;
    bookingRef: string | undefined;
    onRetry?: () => void;
}

const EligibilityBlock: React.FC<EligibilityBlockProps> = ({ error, bookingRef, onRetry }) => {
    const getConfig = () => {
        switch (error.type) {
            case "expired":
                return {
                    bg: "bg-orange-50",
                    border: "border-orange-200",
                    iconBg: "bg-orange-100",
                    iconColor: "text-orange-600",
                    titleColor: "text-orange-800",
                    messageColor: "text-orange-700",
                    title: "Service Request Period Expired",
                    icon: <ClockIcon />,
                    hint: "💡 Service Requests are valid for a specific period after purchase. Please contact support if you believe this is an error.",
                };
            case "limit_reached":
                return {
                    bg: "bg-red-50",
                    border: "border-red-200",
                    iconBg: "bg-red-100",
                    iconColor: "text-red-600",
                    titleColor: "text-red-800",
                    messageColor: "text-red-700",
                    title: "Service Request Limit Reached",
                    icon: <BanIcon />,
                    hint: "💡 Each Care+ subscription includes a limited number of Service Requests. You have used all available Service Requests.",
                };
            case "gap_restriction":
                return {
                    bg: "bg-blue-50",
                    border: "border-blue-200",
                    iconBg: "bg-blue-100",
                    iconColor: "text-blue-600",
                    titleColor: "text-blue-800",
                    messageColor: "text-blue-700",
                    title: "Service Request Not Yet Available",
                    icon: <CalendarIcon />,
                    hint: null,
                };
            case "invalid_booking":
                return {
                    bg: "bg-gray-50",
                    border: "border-gray-200",
                    iconBg: "bg-gray-100",
                    iconColor: "text-gray-600",
                    titleColor: "text-gray-800",
                    messageColor: "text-gray-700",
                    title: "Invalid Booking",
                    icon: <ErrorIcon />,
                    hint: null,
                };
            case "network":
                return {
                    bg: "bg-purple-50",
                    border: "border-purple-200",
                    iconBg: "bg-purple-100",
                    iconColor: "text-purple-600",
                    titleColor: "text-purple-800",
                    messageColor: "text-purple-700",
                    title: "Connection Error",
                    icon: <ErrorIcon />,
                    hint: "💡 Please check your internet connection and try again.",
                };
            default:
                return {
                    bg: "bg-red-50",
                    border: "border-red-200",
                    iconBg: "bg-red-100",
                    iconColor: "text-red-600",
                    titleColor: "text-red-800",
                    messageColor: "text-red-700",
                    title: "Error",
                    icon: <ErrorIcon />,
                    hint: null,
                };
        }
    };
    const config = getConfig();

    return (
        <div className="bg-white shadow-xl rounded-lg p-4 sm:p-8 w-full max-w-md text-gray-900">
            <div className="text-center mb-4">
                <h2 className="text-xl sm:text-2xl font-bold mb-1">Care+ Service</h2>
                <p className="text-xs sm:text-sm text-gray-500">
                    Care+ Id: <span className="font-semibold break-all">{bookingRef}</span>
                </p>
            </div>

            <div className={`${config.bg} ${config.border} border rounded-xl p-4 sm:p-6`}>
                <div className="flex justify-center mb-4">
                    <div className={`${config.iconBg} ${config.iconColor} p-3 sm:p-4 rounded-full`}>{config.icon}</div>
                </div>
                <h3 className={`${config.titleColor} text-lg sm:text-xl font-bold text-center mb-3`}>{config.title}</h3>
                <p className={`${config.messageColor} text-sm text-center mb-4 break-words`}>{sanitizeClaimText(error.message)}</p>

                {error.type === "gap_restriction" && error.nextClaimDate && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 mb-4 border border-blue-200">
                        <p className="text-xs sm:text-sm text-gray-600 text-center mb-1">
                            You can submit your next Service Request on:
                        </p>
                        <p className="text-base sm:text-lg font-semibold text-blue-700 text-center">
                            {error.nextClaimDate.toLocaleDateString("en-IN", {
                                weekday: "long",
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                            })}
                        </p>
                    </div>
                )}

                {config.hint && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                        <p className="text-xs sm:text-sm text-gray-600 text-center">{config.hint}</p>
                    </div>
                )}

                {onRetry && (
                    <button
                        type="button"
                        onClick={onRetry}
                        className="mt-4 w-full bg-gray-800 hover:bg-gray-900 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                        </svg>
                        Try Again
                    </button>
                )}
            </div>
        </div>
    );
};

const StageStatusCard: React.FC<{
    bookingRef?: string;
    title: string;
    message: string;
    tone?: "blue" | "purple" | "green";
}> = ({ bookingRef, title, message, tone = "blue" }) => {
    const tones = {
        blue: {
            bg: "bg-blue-50",
            border: "border-blue-200",
            iconBg: "bg-blue-100",
            iconColor: "text-blue-600",
            title: "text-blue-800",
            msg: "text-blue-700",
        },
        purple: {
            bg: "bg-purple-50",
            border: "border-purple-200",
            iconBg: "bg-purple-100",
            iconColor: "text-purple-600",
            title: "text-purple-800",
            msg: "text-purple-700",
        },
        green: {
            bg: "bg-green-50",
            border: "border-green-200",
            iconBg: "bg-green-100",
            iconColor: "text-green-600",
            title: "text-green-800",
            msg: "text-green-700",
        },
    }[tone];

    return (
        <div className="bg-white shadow-xl rounded-lg p-4 sm:p-8 w-full max-w-md text-gray-900">
            <div className="text-center mb-4">
                <h2 className="text-xl sm:text-2xl font-bold mb-1">Care+ Service</h2>
                <p className="text-xs sm:text-sm text-gray-500">
                    Care+ Id: <span className="font-semibold break-all">{bookingRef}</span>
                </p>
            </div>
            <div className={`${tones.bg} ${tones.border} border rounded-xl p-4 sm:p-6`}>
                <div className="flex justify-center mb-4">
                    <div className={`${tones.iconBg} ${tones.iconColor} p-3 sm:p-4 rounded-full`}>
                        <ClockIcon />
                    </div>
                </div>
                <h3 className={`${tones.title} text-lg sm:text-xl font-bold text-center mb-2`}>{title}</h3>
                <p className={`${tones.msg} text-sm sm:text-base text-center`}>{message}</p>
            </div>
        </div>
    );
};

// ============================================
// ENTITLEMENT PRODUCT LISTING COMPONENT
// ============================================
const EntitlementProductList: React.FC<{ entitlementId: string }> = ({ entitlementId }) => {
    const navigate = useNavigate();
    const [data, setData] = useState<EntitlementData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;

        const fetchProducts = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/sale/entitlement/${entitlementId}/products`, {
                    cache: "no-store",
                });

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    if (isMounted) setError(errData.error || "Failed to load subscription products");
                    return;
                }

                const json: EntitlementData = await res.json();
                if (isMounted) setData(json);
            } catch {
                if (isMounted) setError("Network error. Please check your connection.");
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchProducts();
        return () => {
            isMounted = false;
        };
    }, [entitlementId]);

    if (loading) {
        return (
            <div className="bg-white shadow-xl rounded-lg p-6 sm:p-8 text-center max-w-md w-full">
                <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-700 font-medium text-sm sm:text-base">Loading your Care+ products...</p>
                <p className="text-gray-500 text-xs sm:text-sm mt-1">Please wait a moment</p>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="bg-white shadow-xl rounded-lg p-4 sm:p-8 w-full max-w-md text-gray-900">
                <div className="text-center mb-4">
                    <h2 className="text-xl sm:text-2xl font-bold mb-2">Care+ Service</h2>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 sm:p-6 text-center">
                    <div className="flex justify-center mb-3">
                        <div className="bg-red-100 text-red-600 p-3 sm:p-4 rounded-full">
                            <ErrorIcon />
                        </div>
                    </div>
                    <h3 className="text-red-800 text-lg sm:text-xl font-bold mb-2">Subscription Not Found</h3>
                    <p className="text-red-700 text-sm sm:text-base">{error || "Invalid subscription ID"}</p>
                </div>
            </div>
        );
    }

    const createdProducts = data.products.filter((p) => p.status === "created");
    const exchangedProducts = data.products.filter((p) => p.status === "exchanged");

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return null;
        return d.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const formatFullDate = (dateStr: string | null) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return null;
        return d.toLocaleDateString("en-IN", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    };

    return (
        <div className="bg-white shadow-xl rounded-lg p-4 sm:p-8 w-full max-w-lg text-gray-900">
            <div className="text-center mb-6">
                <h2 className="text-xl sm:text-2xl font-bold mb-1">Care+ Subscription</h2>
                {data.customerName && <p className="text-gray-600 font-medium text-sm sm:text-base">{data.customerName}</p>}
                
                <div className="flex items-center justify-center gap-2 sm:gap-3 mt-3 text-xs sm:text-sm text-gray-500 flex-wrap text-center">
                    <span>
                        {createdProducts.length} product{createdProducts.length !== 1 ? "s" : ""}
                    </span>
                    <span>•</span>
                    <span>
                        {data.maxClaims} Service Request{data.maxClaims !== 1 ? "s" : ""}
                    </span>
                    {data.expiresAt && (
                        <>
                            <span>•</span>
                            <span>Expires {formatDate(data.expiresAt)}</span>
                        </>
                    )}
                </div>

                <div className="mt-2 text-sm sm:text-base font-semibold text-blue-700">
                    {data.remainingClaims} Service{data.remainingClaims !== 1 ? 's' : ''} Remaining
                </div>

                {data.globalCooldownActive && data.nextClaimDate && data.remainingClaims > 0 && (
                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 flex flex-col items-center justify-center">
                        <div className="flex items-center gap-2 text-blue-800 font-semibold mb-1 text-sm sm:text-base">
                            <CalendarIcon />
                            <span>Service Request Not Yet Available</span>
                        </div>
                        <p className="text-xs sm:text-sm text-blue-700 text-center">
                            Next service request available on: <br/> 
                            <span className="font-semibold">{formatFullDate(data.nextClaimDate)}</span>
                        </p>
                    </div>
                )}

                {data.remainingClaims === 0 && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 flex flex-col items-center justify-center">
                        <div className="flex items-center gap-2 text-red-800 font-semibold mb-1 text-sm sm:text-base">
                            <BanIcon />
                            <span>Limit Reached</span>
                        </div>
                        <p className="text-xs sm:text-sm text-red-700 text-center">
                            You have used all available Service Requests for this subscription.
                        </p>
                    </div>
                )}
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-3">
                {createdProducts.map((product) => {
                    const isLimitReached = data.remainingClaims === 0;
                    const isCooldown = data.globalCooldownActive;
                    const isDisabled = product.hasActiveClaim || isCooldown || isLimitReached;
                    
                    let buttonText = "Request Service";
                    if (product.hasActiveClaim) buttonText = "In Progress";
                    else if (isLimitReached) buttonText = "Limit Reached";
                    else if (isCooldown) buttonText = "Cooldown Active";

                    return (
                        <div
                            key={product.saleId}
                            className={`border rounded-xl p-3.5 sm:p-4 transition-all ${
                                isDisabled ? 'border-gray-200 bg-gray-50' : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
                            }`}
                        >
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                                <div className="flex-1 min-w-0 w-full">
                                    <div className="flex items-start sm:items-center gap-2 flex-wrap">
                                        <h3 className={`font-semibold break-words text-sm sm:text-base ${isDisabled ? 'text-gray-500' : 'text-gray-900'}`}>
                                            {product.productName}
                                        </h3>
                                        {product.isReplacement && (
                                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium flex-shrink-0 mt-0.5 sm:mt-0">
                                                🔄 Replacement
                                            </span>
                                        )}
                                        {product.hasActiveClaim && (
                                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium flex-shrink-0 mt-0.5 sm:mt-0">
                                                In Service
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs sm:text-sm text-gray-500 mt-2 flex flex-wrap gap-x-3 gap-y-1 break-all sm:break-words">
                                        {product.color && <span>{product.color}</span>}
                                        {product.size && <span>Size {product.size}</span>}
                                        {product.transactionDate && <span>{formatDate(product.transactionDate)}</span>}
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => navigate(`/schedule/${product.saleId}`)}
                                    disabled={isDisabled}
                                    className={`w-full sm:w-auto flex-shrink-0 text-xs sm:text-sm font-medium px-4 py-2.5 sm:py-2 rounded-lg transition-colors whitespace-nowrap mt-1 sm:mt-0 ${
                                        isDisabled 
                                        ? "bg-gray-300 text-gray-500 cursor-not-allowed" 
                                        : "bg-blue-600 hover:bg-blue-700 text-white"
                                    }`}
                                >
                                    {buttonText}
                                </button>
                            </div>
                        </div>
                    );
                })}

                {exchangedProducts.length > 0 && (
                    <>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2">Exchanged</p>
                        {exchangedProducts.map((product) => (
                            <div
                                key={product.saleId}
                                className="border border-gray-100 rounded-xl p-3.5 sm:p-4 bg-gray-50 opacity-60"
                            >
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                    <div className="flex-1 min-w-0 w-full">
                                        <div className="flex items-start sm:items-center gap-2 flex-wrap">
                                            <h3 className="font-semibold text-gray-500 line-through break-words text-sm sm:text-base">
                                                {product.productName}
                                            </h3>
                                            <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-medium flex-shrink-0 mt-0.5 sm:mt-0">
                                                Exchanged
                                            </span>
                                        </div>
                                        <div className="text-xs sm:text-sm text-gray-400 mt-2 flex flex-wrap gap-x-3 gap-y-1 break-all sm:break-words">
                                            {product.color && <span>{product.color}</span>}
                                            {product.size && <span>Size {product.size}</span>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>

            {createdProducts.length === 0 && exchangedProducts.length === 0 && (
                <div className="text-center py-8 text-gray-400 text-sm">
                    <p>No products found under this subscription.</p>
                </div>
            )}
        </div>
    );
};

export default function ScheduleForm() {
    const { bookingRef } = useParams();
    const claimTransaction = useRecoilValue(transactionClaimAtom);
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    const isResubmit = searchParams.get("resubmit") === "true";
    const resubmitClaimId = searchParams.get("claimId");

    const [isLoadingPrefill, setIsLoadingPrefill] = useState(false);
    const [prefillLoaded, setPrefillLoaded] = useState(false);
    const [rejectionReason, setRejectionReason] = useState<string | null>(null);

    const [acceptedSlotImages, setAcceptedSlotImages] = useState<(string | null)[]>([]);

    const [isCheckingEligibility, setIsCheckingEligibility] = useState(true);
    const [eligibilityError, setEligibilityError] = useState<ApiError | null>(null);
    const [eligibilityRetryKey, setEligibilityRetryKey] = useState(0);

    const [latestClaim, setLatestClaim] = useState<LatestClaimInfo | null>(null);
    const [completedClaims, setCompletedClaims] = useState(0);
    const [maxClaimsFromApi, setMaxClaimsFromApi] = useState<number | null>(null);
    const [blockedSubServices, setBlockedSubServices] = useState<string[]>([]);

    const [step, setStep] = useState(1);
    const [pickupDate, setPickupDate] = useState<Date | null>(null);

    const minPickupDate = React.useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + 1);
        return d;
    }, []);

    const [category, setCategory] = useState("");
    // MULTI-SUBSERVICE STATE: Store selected sub-service labels as an array
    const [subService, setSubService] = useState<string[]>([]);
    const [comments, setComments] = useState("");
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [activeServiceData, setActiveServiceData] = useState<ServiceData>(serviceData);
    const [isLoadingServices, setIsLoadingServices] = useState(false);

    const [isLookingUpPincode, setIsLookingUpPincode] = useState(false);
    const [pincodeAutoFilled, setPincodeAutoFilled] = useState(false);
    const [pincodeError, setPincodeError] = useState("");

    const [isLookingUpDropPincode, setIsLookingUpDropPincode] = useState(false);
    const [dropPincodeAutoFilled, setDropPincodeAutoFilled] = useState(false);
    const [dropPincodeError, setDropPincodeError] = useState("");

    const [dropSameAsPickup, setDropSameAsPickup] = useState(true);

    const [apiError, setApiError] = useState<ApiError | null>(null);
    const [showErrorModal, setShowErrorModal] = useState(false);

    const [otpValue, setOtpValue] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [otpBlocked, setOtpBlocked] = useState(false);
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
    const [otpError, setOtpError] = useState("");
    const [otpSuccess, setOtpSuccess] = useState("");
    const [otpResendTimer, setOtpResendTimer] = useState(0);

    const [formData, setFormData] = useState({
        name: "",
        phone: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        dropAddress1: "",
        dropAddress2: "",
        dropCity: "",
        dropState: "",
        dropZip: "",
        email: "",
    });

    const showEntitlementList = isEntitlementId(bookingRef);

    // Dynamic calculations based on multi-subservice selection
    const remainingClaims = Math.max((maxClaimsFromApi ?? 1) - completedClaims, 1);
    const requiredPhotoCount = 3 + subService.length; // 3 general required + 1 per sub-service
    const maxPhotoCount = 4 + subService.length * 2; // 4 general max + 2 per sub-service

    useEffect(() => {
        if (dropSameAsPickup) {
            setFormData((prev) => ({
                ...prev,
                dropAddress1: prev.address1,
                dropAddress2: prev.address2,
                dropCity: prev.city,
                dropState: prev.state,
                dropZip: prev.zip,
            }));
        }
    }, [dropSameAsPickup, formData.address1, formData.address2, formData.city, formData.state, formData.zip]);

    useEffect(() => {
        if (otpResendTimer <= 0) return;
        const timer = setTimeout(() => setOtpResendTimer((t) => t - 1), 1000);
        return () => clearTimeout(timer);
    }, [otpResendTimer]);

    useEffect(() => {
        setOtpSent(false);
        setOtpVerified(false);
        setOtpBlocked(false);
        setOtpValue("");
        setOtpError("");
        setOtpSuccess("");
    }, [formData.phone]);

    useEffect(() => {
        if (!bookingRef || showEntitlementList) {
            setIsCheckingEligibility(false);
            return;
        }

        let isMounted = true;

        const run = async (retriesLeft = 3, delay = 1000) => {
            setIsCheckingEligibility(true);
            setEligibilityError(null);
            setLatestClaim(null);
            setBlockedSubServices([]);

            try {
                const eligRes = await fetch(`${API_BASE_URL}/api/claim/eligibility/${bookingRef}`, {
                    cache: "no-store",
                });

                if (!eligRes.ok) {
                    let errorData: any = {};
                    try {
                        errorData = await eligRes.json();
                    } catch {
                        /* ignore json error */
                    }

                    if (eligRes.status >= 500 && retriesLeft > 0) {
                        await new Promise((res) => setTimeout(res, delay));
                        if (!isMounted) return;
                        return run(retriesLeft - 1, delay * 1.5);
                    }

                    const parsed = parseApiError(errorData);
                    if (isMounted) {
                        setEligibilityError(
                            parsed.message
                                ? parsed
                                : { type: "unknown", message: "Failed to check Service Request eligibility" },
                        );
                    }
                    return;
                }

                const elig = await eligRes.json();
                if (!isMounted) return;

                if (typeof elig.maxClaims === "number") setMaxClaimsFromApi(elig.maxClaims);
                if (typeof elig.usedClaims === "number") setCompletedClaims(elig.usedClaims);
                if (Array.isArray(elig.blockedSubServices)) setBlockedSubServices(elig.blockedSubServices);

                let latest: LatestClaimInfo | null = null;
                try {
                    const claimsRes = await fetch(`${API_BASE_URL}/api/claim`, { cache: "no-store" });
                    if (claimsRes.ok) {
                        const all = await claimsRes.json();
                        const mine = (all || [])
                            .filter((c: any) => String(c.bookingRef) === String(bookingRef))
                            .filter((c: any) => c.claimStatus && c.claimStatus !== "CANCELLED")
                            .sort((a: any, b: any) => Number(b.id) - Number(a.id));

                        if (mine[0]) {
                            latest = {
                                id: mine[0].id,
                                claimStatus: mine[0].claimStatus as ClaimStatus,
                            };
                            if (isMounted) setLatestClaim(latest);
                        }
                    }
                } catch {
                    // Ignore
                }

                if (!isMounted) return;

                const activeInProgress =
                    !!latest && latest.claimStatus !== "DELIVERED" && latest.claimStatus !== "REJECTED";

                if (activeInProgress) {
                    setEligibilityError(null);
                    return;
                }

                if (!elig.eligible) {
                    const errorType: ApiError["type"] =
                        elig.reason === "expired"
                            ? "expired"
                            : elig.reason === "limit_reached"
                              ? "limit_reached"
                              : elig.reason === "gap_restriction"
                                ? "gap_restriction"
                                : elig.reason === "invalid_booking"
                                  ? "invalid_booking"
                                  : "unknown";

                    setEligibilityError({
                        type: errorType,
                        message: sanitizeClaimText(elig.message || "Failed to check Service Request eligibility"),
                        nextClaimDate: elig.nextClaimDate ? new Date(elig.nextClaimDate) : null,
                    });
                }
            } catch {
                if (retriesLeft > 0) {
                    await new Promise((res) => setTimeout(res, delay));
                    if (!isMounted) return;
                    return run(retriesLeft - 1, delay * 1.5);
                }

                if (isMounted) {
                    setEligibilityError({
                        type: "network",
                        message: "Failed to check Service Request eligibility. Please check connection.",
                    });
                }
            } finally {
                if (isMounted) setIsCheckingEligibility(false);
            }
        };

        run();

        return () => {
            isMounted = false;
        };
    }, [bookingRef, eligibilityRetryKey, showEntitlementList]);

    useEffect(() => {
        if (showEntitlementList) return;
        if (!isResubmit || !resubmitClaimId || isCheckingEligibility || eligibilityError || !bookingRef) return;

        const prefillForm = async () => {
            setIsLoadingPrefill(true);
            try {
                const res = await fetch(`${API_BASE_URL}/api/claim/${resubmitClaimId}`);
                if (!res.ok) return;

                const data = await res.json();
                if (String(data.bookingRef) !== String(bookingRef)) return;

                setFormData({
                    name: data.name || "",
                    phone: data.phone || "",
                    email: data.email || "",
                    address1: data.address1 || "",
                    address2: data.address2 || "",
                    city: data.city || "",
                    state: data.state || "",
                    zip: data.zip || "",
                    dropAddress1: data.dropAddress1 || "",
                    dropAddress2: data.dropAddress2 || "",
                    dropCity: data.dropCity || "",
                    dropState: data.dropState || "",
                    dropZip: data.dropZip || "",
                });

                const isSame =
                    (data.address1 || "") === (data.dropAddress1 || "") &&
                    (data.city || "") === (data.dropCity || "") &&
                    (data.zip || "") === (data.dropZip || "");
                setDropSameAsPickup(isSame);

                if (data.category) setCategory(data.category);
                if (data.subService) {
                    // Split comma-separated string into array for multi-subservice state
                    const prefilled = data.subService
                        .split(",")
                        .map((s: string) => s.trim())
                        .filter(Boolean);
                    setSubService(prefilled);
                }
                if (data.comments) setComments(data.comments);
                if (data.rejectionReason) setRejectionReason(data.rejectionReason);

                const fileStatuses = data.fileStatuses || {};
                const files: string[] = data.files || [];
                const slotAligned: (string | null)[] = Array(files.length).fill(null);

                files.forEach((url, index) => {
                    const status = getFileStatus(url, fileStatuses);
                    if (status === "ACCEPTED") {
                        slotAligned[index] = url;
                    }
                });

                setAcceptedSlotImages(slotAligned);
                setPrefillLoaded(true);
            } catch {
                // silent
            } finally {
                setIsLoadingPrefill(false);
            }
        };

        prefillForm();
    }, [isResubmit, resubmitClaimId, isCheckingEligibility, eligibilityError, bookingRef, showEntitlementList]);

    useEffect(() => {
        if (showEntitlementList) return;
        if (!bookingRef || eligibilityError) return;

        const fetchServices = async () => {
            setIsLoadingServices(true);
            try {
                const res = await fetch(`${API_BASE_URL}/api/bookingRef/memberUuid/lookup`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ bookingRef: Number(bookingRef) }),
                });
                if (!res.ok) return;
                const { memberUuid } = await res.json();

                const mapCategories = (data: { category: string; subCategories: string[] }[]): ServiceData => {
                    const mapped: ServiceData = {};
                    data.forEach(({ category: cat, subCategories }) => {
                        mapped[cat] = subCategories.map((label) => ({
                            id: `${cat.toLowerCase().replace(/\s+/g, "-")}-${label
                                .toLowerCase()
                                .replace(/[\s&()/]+/g, "-")
                                .replace(/-+/g, "-")
                                .replace(/-$/, "")}`,
                            label,
                        }));
                    });
                    return mapped;
                };

                if (ZEESH_MEMBER_UUIDS.has(memberUuid)) {
                    const catRes = await fetch(`${API_BASE_URL}/api/zeesh/category`);
                    if (catRes.ok) setActiveServiceData(mapCategories(await catRes.json()));
                } else if (CONVERSE_MEMBER_UUIDS.has(memberUuid)) {
                    const catRes = await fetch(`${API_BASE_URL}/api/converse/category`);
                    if (catRes.ok) setActiveServiceData(mapCategories(await catRes.json()));
                } else if (BEHNO_MEMBER_UUIDS.has(memberUuid)) {
                    const catRes = await fetch(`${API_BASE_URL}/api/behno/category`);
                    if (catRes.ok) setActiveServiceData(mapCategories(await catRes.json()));
                } else if (OROH_MEMBER_UUIDS.has(memberUuid)) {
                    const catRes = await fetch(`${API_BASE_URL}/api/oroh/category`);
                    if (catRes.ok) setActiveServiceData(mapCategories(await catRes.json()));
                }
            } catch {
                // fallback default serviceData
            } finally {
                setIsLoadingServices(false);
            }
        };

        fetchServices();
    }, [bookingRef, eligibilityError, showEntitlementList]);

    // Handle toggling sub-services (Checkbox behavior capped at remaining claim count)
    const toggleSubService = (label: string) => {
        const isCurrentlySelected = subService.includes(label);

        if (isCurrentlySelected) {
            setSubService((prev) => prev.filter((s) => s !== label));
        } else {
            if (subService.length >= remainingClaims) {
                showErrorAlert({
                    type: "limit_reached",
                    message: `You have ${remainingClaims} remaining claim(s) in your allowance. You can select at most ${remainingClaims} service(s) at once.`,
                });
                return;
            }
            setSubService((prev) => [...prev, label]);
        }
    };

    const isStep1Valid = !!(pickupDate && category && subService.length > 0);
    const isStep2Valid = !!(
        formData.name &&
        formData.email &&
        formData.phone &&
        formData.address1 &&
        formData.city &&
        formData.state &&
        formData.zip &&
        formData.dropAddress1 &&
        formData.dropCity &&
        formData.dropState &&
        formData.dropZip
    );

    const filterPickupDate = (date: Date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d.getTime() >= minPickupDate.getTime();
    };

    const handleCloseErrorModal = () => {
        setShowErrorModal(false);
        setApiError(null);
    };

    const showErrorAlert = (error: ApiError) => {
        setApiError(error);
        setShowErrorModal(true);
    };

    const handleZipChange = async (zip: string) => {
        const cleanZip = zip.replace(/\D/g, "").slice(0, 6);
        setFormData((prev) => ({ ...prev, zip: cleanZip }));
        setPincodeAutoFilled(false);
        setPincodeError("");

        if (cleanZip.length === 6) {
            setIsLookingUpPincode(true);
            try {
                const res = await fetch(`https://api.postalpincode.in/pincode/${cleanZip}`);
                const data = await res.json();
                if (Array.isArray(data) && data[0]?.Status === "Success" && data[0].PostOffice?.length > 0) {
                    const po = data[0].PostOffice[0];
                    setFormData((prev) => ({
                        ...prev,
                        zip: cleanZip,
                        city: po.District || prev.city,
                        state: po.State || prev.state,
                    }));
                    setPincodeAutoFilled(true);
                } else {
                    setPincodeError("Pincode not found — please enter city/state manually");
                }
            } catch {
                setPincodeError("Could not auto-detect — please enter city/state manually");
            } finally {
                setIsLookingUpPincode(false);
            }
        }
    };

    const handleDropZipChange = async (zip: string) => {
        const cleanZip = zip.replace(/\D/g, "").slice(0, 6);
        setFormData((prev) => ({ ...prev, dropZip: cleanZip }));
        setDropPincodeAutoFilled(false);
        setDropPincodeError("");

        if (cleanZip.length === 6) {
            setIsLookingUpDropPincode(true);
            try {
                const res = await fetch(`https://api.postalpincode.in/pincode/${cleanZip}`);
                const data = await res.json();
                if (Array.isArray(data) && data[0]?.Status === "Success" && data[0].PostOffice?.length > 0) {
                    const po = data[0].PostOffice[0];
                    setFormData((prev) => ({
                        ...prev,
                        dropZip: cleanZip,
                        dropCity: po.District || prev.dropCity,
                        dropState: po.State || prev.dropState,
                    }));
                    setDropPincodeAutoFilled(true);
                } else {
                    setDropPincodeError("Pincode not found — please enter city/state manually");
                }
            } catch {
                setDropPincodeError("Could not auto-detect — please enter city/state manually");
            } finally {
                setIsLookingUpDropPincode(false);
            }
        }
    };

    const handleSendOtp = async () => {
        setOtpError("");
        setOtpSuccess("");
        setOtpBlocked(false);
        setOtpValue("");

        if (!formData.phone || formData.phone.trim() === "") {
            setOtpError("Please enter your phone number first");
            return;
        }

        setIsSendingOtp(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/claim/send-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ phone: formData.phone }),
            });
            const data = await res.json();
            if (res.ok) {
                setOtpSent(true);
                setOtpResendTimer(60);
                setOtpSuccess("OTP sent to your WhatsApp number!");
            } else {
                setOtpError(data.error || "Failed to send OTP. Please try again.");
            }
        } catch {
            setOtpError("Network error. Please check your connection.");
        } finally {
            setIsSendingOtp(false);
        }
    };

    const handleVerifyOtp = async () => {
        setOtpError("");
        setOtpSuccess("");

        if (!otpValue || otpValue.trim().length !== 6) {
            setOtpError("Please enter the 6-digit OTP");
            return;
        }

        setIsVerifyingOtp(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/claim/verify-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ phone: formData.phone, otp: otpValue }),
            });
            const data = await res.json();
            if (res.ok && data.verified) {
                setOtpVerified(true);
                setOtpSuccess("✓ Phone number verified successfully!");
                setOtpError("");
            } else {
                if (data.error?.toLowerCase().includes("too many wrong attempts")) setOtpBlocked(true);
                setOtpError(data.error || "Invalid OTP. Please try again.");
            }
        } catch {
            setOtpError("Network error. Please check your connection.");
        } finally {
            setIsVerifyingOtp(false);
        }
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setApiError(null);
        setShowErrorModal(false);

        if (!otpVerified) {
            showErrorAlert({ type: "otp", message: "Please verify your phone number with OTP before submitting." });
            return;
        }

        const keptCount = acceptedSlotImages.filter(Boolean).length;
        const totalImages = keptCount + uploadedFiles.length;

        if (totalImages < requiredPhotoCount) {
            showErrorAlert({
                type: "validation",
                message: `Please upload all ${requiredPhotoCount} required photos for your selected services. You have ${totalImages} of ${requiredPhotoCount}.`,
            });
            return;
        }

        if (totalImages > maxPhotoCount) {
            showErrorAlert({ type: "validation", message: `You can upload a maximum of ${maxPhotoCount} photos.` });
            return;
        }

        if (!isStep1Valid || !isStep2Valid) {
            showErrorAlert({ type: "validation", message: "Please complete all required fields before submitting." });
            return;
        }

        let formattedPickupDate = "";
        if (pickupDate) {
            const y = pickupDate.getFullYear();
            const m = String(pickupDate.getMonth() + 1).padStart(2, "0");
            const d = String(pickupDate.getDate()).padStart(2, "0");
            formattedPickupDate = `${y}/${m}/${d}`;
        }

        const payload = new FormData();
        payload.append("bookingRef", bookingRef || "");
        payload.append("pickupDate", formattedPickupDate);
        payload.append("category", category);
        // Send comma-separated sub-services string to backend
        payload.append("subService", subService.join(","));
        payload.append("comments", comments);
        if (claimTransaction) payload.append("transactionId", String(claimTransaction));

        if (isResubmit && resubmitClaimId) {
            payload.append("previousClaimId", resubmitClaimId);
            payload.append("existingFilesBySlot", JSON.stringify(acceptedSlotImages));
            acceptedSlotImages.forEach((url) => {
                if (url) payload.append("existingFiles", url);
            });
        }

        Object.entries(formData).forEach(([key, value]) => payload.append(key, value));
        uploadedFiles.forEach((file) => payload.append("files", file));

        setIsSubmitting(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/claim/create`, {
                method: "POST",
                body: payload,
            });

            if (response.ok) {
                navigate("/claim-success");
            } else {
                const errorData = await response.json();
                showErrorAlert(parseApiError(errorData));
            }
        } catch {
            showErrorAlert({
                type: "network",
                message: "Unable to connect to the server. Please check your internet connection and try again.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (showEntitlementList) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-black px-2 sm:px-4 py-6 sm:py-10">
                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: scale(0.95) translateY(-10px); }
                        to   { opacity: 1; transform: scale(1) translateY(0); }
                    }
                `}</style>
                <EntitlementProductList entitlementId={bookingRef!} />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-black px-2 sm:px-4 py-6 sm:py-10">
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: scale(0.95) translateY(-10px); }
                    to   { opacity: 1; transform: scale(1) translateY(0); }
                }
            `}</style>

            {isCheckingEligibility && (
                <div className="bg-white shadow-xl rounded-lg p-6 sm:p-8 text-center max-w-md w-full">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-700 font-medium text-sm sm:text-base">Verifying your Care+ eligibility...</p>
                    <p className="text-gray-500 text-xs sm:text-sm mt-1">Please wait a moment</p>
                </div>
            )}

            {!isCheckingEligibility &&
                (() => {
                    const status = latestClaim?.claimStatus;
                    const max = maxClaimsFromApi ?? 1;
                    const used = completedClaims;

                    if (status && IN_REVIEW_STATUSES.includes(status)) {
                        return (
                            <StageStatusCard
                                bookingRef={bookingRef}
                                title="Service Request Received"
                                message="Your Service Request is under review."
                                tone="blue"
                            />
                        );
                    }

                    if (status && IN_CARE_STATUSES.includes(status)) {
                        return (
                            <StageStatusCard
                                bookingRef={bookingRef}
                                title="In Care"
                                message="In Care and will be on its way back soon"
                                tone="purple"
                            />
                        );
                    }

                    if (eligibilityError) {
                        if (eligibilityError.type === "limit_reached") {
                            const displayMax = maxClaimsFromApi ?? (used > 0 ? used : 1);
                            const displayUsed = used > 0 ? used : displayMax;
                            return (
                                <StageStatusCard
                                    bookingRef={bookingRef}
                                    title="Service Request Complete"
                                    message={`${displayUsed} of ${displayMax} Service Request${displayMax === 1 ? "" : "s"} complete`}
                                    tone="green"
                                />
                            );
                        }
                        return (
                            <EligibilityBlock
                                error={eligibilityError}
                                bookingRef={bookingRef}
                                onRetry={() => setEligibilityRetryKey((prev) => prev + 1)}
                            />
                        );
                    }

                    return (
                        <div className="bg-white shadow-xl rounded-lg p-4 sm:p-8 w-full max-w-2xl text-gray-900">
                            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-1 sm:mb-2">Care+ Service</h2>
                            <p className="text-center text-gray-600 text-sm sm:text-base mb-4 sm:mb-6">Premium care for your premium items</p>
                            <div className="text-xs sm:text-sm text-center text-gray-500 mb-4">
                                Care+ Id: <span className="font-semibold break-all">{bookingRef}</span>
                            </div>

                            <p className="text-xs sm:text-sm font-medium text-blue-600">Step {step} of 2</p>
                            <div className="w-full bg-gray-200 rounded-full h-1 mt-1 mb-4">
                                <div
                                    className="bg-blue-500 h-1 rounded-full transition-all duration-300"
                                    style={{ width: `${step * 50}%` }}
                                />
                            </div>

                            {isResubmit && isLoadingPrefill && (
                                <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                                    <svg
                                        className="animate-spin h-4 w-4 text-blue-500 flex-shrink-0"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        />
                                    </svg>
                                    <p className="text-gray-500 text-xs sm:text-sm">
                                        Loading your previous Service Request details...
                                    </p>
                                </div>
                            )}

                            {isResubmit && prefillLoaded && rejectionReason && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4">
                                    <div className="flex items-start gap-2">
                                        <span className="text-red-500 flex-shrink-0 mt-0.5">❌</span>
                                        <div>
                                            <p className="text-red-800 font-semibold text-xs sm:text-sm">
                                                Previous Service Request was rejected
                                            </p>
                                            <p className="text-red-700 text-xs sm:text-sm mt-0.5">
                                                Reason: <span className="font-medium italic break-words">{rejectionReason}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleFormSubmit} className="space-y-4 sm:space-y-6">
                                {step === 1 && (
                                    <>
                                        <div>
                                            <label className="font-semibold block text-gray-800 text-sm sm:text-base mb-1">
                                                Pick-up Date
                                            </label>
                                            <DatePicker
                                                selected={pickupDate}
                                                onChange={setPickupDate}
                                                minDate={minPickupDate}
                                                filterDate={filterPickupDate}
                                                className="w-full border p-2.5 sm:p-3 rounded-md text-sm sm:text-base"
                                                placeholderText="Select a date"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                                Earliest available:{" "}
                                                {minPickupDate.toLocaleDateString("en-IN", {
                                                    weekday: "short",
                                                    day: "2-digit",
                                                    month: "short",
                                                    year: "numeric",
                                                })}
                                            </p>
                                        </div>

                                        <div>
                                            <label className="block font-medium text-sm sm:text-base mb-1">Select Category</label>
                                            {isLoadingServices ? (
                                                <div className="flex items-center space-x-2 p-3 border rounded-md text-gray-500">
                                                    <svg
                                                        className="animate-spin h-4 w-4"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                    >
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                        />
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                        />
                                                    </svg>
                                                    <span className="text-sm">Loading services...</span>
                                                </div>
                                            ) : (
                                                <select
                                                    value={category}
                                                    onChange={(e) => {
                                                        setCategory(e.target.value);
                                                        setSubService([]);
                                                    }}
                                                    className="w-full p-2.5 sm:p-3 border rounded-md text-sm sm:text-base"
                                                >
                                                    <option value="">Choose a category</option>
                                                    {Object.keys(activeServiceData).map((k) => (
                                                        <option key={k}>{k}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>

                                        {category && !isLoadingServices && (
                                            <div className="pl-1 sm:pl-2 border-l-2 border-gray-200">
                                                <div className="flex items-center justify-between mb-2">
                                                    <p className="text-gray-700 font-medium text-xs sm:text-sm">
                                                        Select services (Checkbox multi-select):
                                                    </p>
                                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-semibold">
                                                        Selected: {subService.length} / {remainingClaims}
                                                    </span>
                                                </div>

                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                                                    {activeServiceData[category]?.map((service) => {
                                                        const isBlocked = blockedSubServices.some((blockedName) => {
                                                            const cleanBlocked = blockedName.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
                                                            const cleanLabel = service.label.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
                                                            const cleanId = service.id.trim().toLowerCase().replace(/[^a-z0-9]/g, "");

                                                            return (
                                                                cleanBlocked === cleanLabel ||
                                                                cleanBlocked === cleanId ||
                                                                cleanBlocked.includes(cleanLabel) ||
                                                                cleanId.includes(cleanBlocked)
                                                            );
                                                        });

                                                        const isSelected = subService.includes(service.label) || subService.includes(service.id);
                                                        const limitReached = subService.length >= remainingClaims && !isSelected;
                                                        const isDisabled = isBlocked || limitReached;

                                                        return (
                                                            <button
                                                                key={service.id}
                                                                type="button"
                                                                disabled={isDisabled}
                                                                onClick={() => toggleSubService(service.label)}
                                                                className={`relative flex items-center justify-between p-3 rounded-xl border-2 text-left transition-all ${
                                                                    isSelected
                                                                        ? "border-blue-600 bg-blue-50 ring-1 ring-blue-600"
                                                                        : "border-gray-200 bg-white hover:border-blue-300"
                                                                } ${isDisabled ? "opacity-50 cursor-not-allowed bg-gray-50" : "cursor-pointer"}`}
                                                            >
                                                                <div className="flex-1 pr-3 min-w-0">
                                                                    <p className={`font-medium text-xs sm:text-sm break-words ${isSelected ? "text-blue-900 font-semibold" : "text-gray-900"}`}>
                                                                        {service.label}
                                                                    </p>
                                                                    {isBlocked && (
                                                                        <p className="text-[10px] text-red-500 font-semibold mt-0.5">Service Availed</p>
                                                                    )}
                                                                    {limitReached && !isBlocked && (
                                                                        <p className="text-[10px] text-amber-600 font-medium mt-0.5">Allowance cap reached</p>
                                                                    )}
                                                                </div>

                                                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                                                                    isSelected ? "bg-blue-600 border-blue-600 text-white" : "border-gray-300 bg-white"
                                                                }`}>
                                                                    {isSelected && (
                                                                        <svg className="w-3.5 h-3.5 stroke-[3]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                                        </svg>
                                                                    )}
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        <button
                                            type="button"
                                            disabled={!isStep1Valid || isLoadingServices}
                                            onClick={() => setStep(2)}
                                            className="w-full bg-blue-600 text-white py-3 rounded-md disabled:bg-gray-300 hover:bg-blue-700 transition-colors text-sm sm:text-base font-medium mt-4"
                                        >
                                            Next →
                                        </button>
                                    </>
                                )}

                                {step === 2 && (
                                    <>
                                        <div>
                                            <label className="block font-medium text-sm sm:text-base mb-1">
                                                Additional Comments (Optional)
                                            </label>
                                            <textarea
                                                value={comments}
                                                onChange={(e) => setComments(e.target.value)}
                                                rows={3}
                                                maxLength={500}
                                                className="w-full p-2.5 sm:p-3 border rounded-md text-sm sm:text-base"
                                                placeholder="Describe any specific damage or issue"
                                            />
                                        </div>

                                        <div>
                                            <label className="block font-medium text-sm sm:text-base mb-2">Pickup Details</label>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                                <input
                                                    placeholder="Full Name"
                                                    className="p-2.5 sm:p-3 border rounded-md text-sm sm:text-base"
                                                    value={formData.name}
                                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                />

                                                <div className="flex items-center border rounded-md overflow-hidden bg-white text-sm sm:text-base">
                                                    <span className="px-2.5 sm:px-3 py-2.5 sm:py-3 bg-gray-100 text-gray-700 font-medium border-r select-none">
                                                        +91
                                                    </span>
                                                    <input
                                                        placeholder="Phone Number"
                                                        maxLength={10}
                                                        inputMode="numeric"
                                                        className="flex-1 p-2.5 sm:p-3 outline-none w-full"
                                                        value={formData.phone}
                                                        onChange={(e) =>
                                                            setFormData({
                                                                ...formData,
                                                                phone: e.target.value.replace(/\D/g, "").slice(0, 10),
                                                            })
                                                        }
                                                    />
                                                </div>
                                            </div>

                                            {formData.phone && formData.phone.length >= 10 && (
                                                <div className="mt-3 border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-3">
                                                    <div className="flex items-center gap-2">
                                                        <svg
                                                            className="w-4 h-4 text-blue-600"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                                                            />
                                                        </svg>
                                                        <p className="font-medium text-gray-800 text-xs sm:text-sm">
                                                            Verify Your Phone Number
                                                        </p>
                                                    </div>

                                                    {!otpVerified && (
                                                        <div className="space-y-2">
                                                            <button
                                                                type="button"
                                                                onClick={handleSendOtp}
                                                                disabled={isSendingOtp || otpResendTimer > 0}
                                                                className="w-full py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 transition-colors text-xs sm:text-sm font-medium"
                                                            >
                                                                {isSendingOtp
                                                                    ? "Sending..."
                                                                    : otpResendTimer > 0
                                                                      ? `Resend OTP in ${otpResendTimer}s`
                                                                      : otpSent
                                                                        ? "Resend OTP"
                                                                        : "Send OTP via WhatsApp"}
                                                            </button>

                                                            {otpSent && !otpBlocked && (
                                                                <div className="space-y-2">
                                                                    <div className="flex items-center gap-2">
                                                                        <input
                                                                            type="text"
                                                                            inputMode="numeric"
                                                                            maxLength={6}
                                                                            placeholder="6-digit OTP"
                                                                            value={otpValue}
                                                                            onChange={(e) => {
                                                                                setOtpValue(
                                                                                    e.target.value
                                                                                        .replace(/\D/g, "")
                                                                                        .slice(0, 6),
                                                                                );
                                                                                setOtpError("");
                                                                            }}
                                                                            className="flex-1 p-2 border rounded-md text-center text-base sm:text-lg font-mono tracking-widest min-w-0"
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            onClick={handleVerifyOtp}
                                                                            disabled={
                                                                                isVerifyingOtp || otpValue.length !== 6
                                                                            }
                                                                            className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 transition-colors text-xs sm:text-sm font-medium whitespace-nowrap"
                                                                        >
                                                                            {isVerifyingOtp ? "Verifying..." : "Verify"}
                                                                        </button>
                                                                    </div>
                                                                    <p className="text-xs text-gray-500 text-center">
                                                                        Check WhatsApp. Code expires in 10 minutes.
                                                                    </p>
                                                                </div>
                                                            )}

                                                            {otpBlocked && (
                                                                <div className="bg-red-50 border border-red-200 rounded-md p-2 text-center">
                                                                    <p className="text-red-700 text-xs font-semibold">
                                                                        🚫 Too many wrong attempts — click Resend OTP
                                                                        above
                                                                    </p>
                                                                </div>
                                                            )}

                                                            {otpError && (
                                                                <p className="text-xs text-red-600 text-center bg-red-50 p-2 rounded-md">
                                                                    ❌ {otpError}
                                                                </p>
                                                            )}
                                                            {otpSuccess && !otpVerified && (
                                                                <p className="text-xs text-green-600 text-center bg-green-50 p-2 rounded-md">
                                                                    ✅ {otpSuccess}
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}

                                                    {otpVerified && (
                                                        <div className="bg-green-50 border border-green-200 rounded-md p-2 text-center">
                                                            <p className="text-green-700 text-xs sm:text-sm font-semibold">
                                                                ✅ Phone number verified
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <input
                                                type="email"
                                                placeholder="Email Address"
                                                className="p-2.5 sm:p-3 border rounded-md w-full mt-2 text-sm sm:text-base"
                                                value={formData.email}
                                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            />

                                            <h3 className="font-semibold text-gray-800 mt-4 mb-2 text-sm sm:text-base">📍 Pickup Address</h3>

                                            <input
                                                placeholder="Address Line 1 (House/Flat No., Building, Street)"
                                                className="p-2.5 sm:p-3 border rounded-md w-full text-sm sm:text-base"
                                                value={formData.address1}
                                                onChange={(e) => setFormData({ ...formData, address1: e.target.value })}
                                            />
                                            <input
                                                placeholder="Address Line 2 (Area, Landmark)"
                                                className="p-2.5 sm:p-3 border rounded-md w-full mt-2 text-sm sm:text-base"
                                                value={formData.address2}
                                                onChange={(e) => setFormData({ ...formData, address2: e.target.value })}
                                            />

                                            <div className="relative mt-2">
                                                <input
                                                    placeholder="Zip Code (6 digits)"
                                                    maxLength={6}
                                                    inputMode="numeric"
                                                    className="p-2.5 sm:p-3 border rounded-md w-full pr-10 text-sm sm:text-base"
                                                    value={formData.zip}
                                                    onChange={(e) => handleZipChange(e.target.value)}
                                                />
                                                {isLookingUpPincode && (
                                                    <svg
                                                        className="animate-spin h-5 w-5 text-blue-500 absolute right-3 top-1/2 -translate-y-1/2"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                    >
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                        />
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                        />
                                                    </svg>
                                                )}
                                                {pincodeAutoFilled && !isLookingUpPincode && (
                                                    <p className="text-xs text-green-600 mt-1">
                                                        ✓ Auto-detected: {formData.city}, {formData.state}
                                                    </p>
                                                )}
                                                {pincodeError && !isLookingUpPincode && (
                                                    <p className="text-xs text-orange-600 mt-1">⚠️ {pincodeError}</p>
                                                )}
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-2">
                                                <input
                                                    placeholder="City"
                                                    className="p-2.5 sm:p-3 border rounded-md w-full text-sm sm:text-base"
                                                    value={formData.city}
                                                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                                />
                                                <input
                                                    placeholder="State"
                                                    className="p-2.5 sm:p-3 border rounded-md w-full text-sm sm:text-base"
                                                    value={formData.state}
                                                    onChange={(e) =>
                                                        setFormData({ ...formData, state: e.target.value })
                                                    }
                                                />
                                            </div>

                                            <label className="flex items-center gap-2 mt-4 p-2.5 sm:p-3 bg-blue-50 border border-blue-200 rounded-md cursor-pointer hover:bg-blue-100 transition-colors">
                                                <input
                                                    type="checkbox"
                                                    checked={dropSameAsPickup}
                                                    onChange={(e) => setDropSameAsPickup(e.target.checked)}
                                                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 flex-shrink-0"
                                                />
                                                <span className="text-xs sm:text-sm font-medium text-blue-900">
                                                    Drop address is the same as Pickup address
                                                </span>
                                            </label>

                                            {!dropSameAsPickup && (
                                                <div className="mt-4 p-3 sm:p-4 border border-gray-300 rounded-lg bg-gray-50">
                                                    <h3 className="font-semibold text-gray-800 mb-3 text-sm sm:text-base">
                                                        🚚 Drop Address
                                                    </h3>

                                                    <input
                                                        placeholder="Drop Address Line 1 (House/Flat No., Building, Street)"
                                                        className="p-2.5 sm:p-3 border rounded-md w-full text-sm sm:text-base"
                                                        value={formData.dropAddress1}
                                                        onChange={(e) =>
                                                            setFormData({ ...formData, dropAddress1: e.target.value })
                                                        }
                                                    />
                                                    <input
                                                        placeholder="Drop Address Line 2 (Area, Landmark)"
                                                        className="p-2.5 sm:p-3 border rounded-md w-full mt-2 text-sm sm:text-base"
                                                        value={formData.dropAddress2}
                                                        onChange={(e) =>
                                                            setFormData({ ...formData, dropAddress2: e.target.value })
                                                        }
                                                    />

                                                    <div className="relative mt-2">
                                                        <input
                                                            placeholder="Drop Zip Code (6 digits)"
                                                            maxLength={6}
                                                            inputMode="numeric"
                                                            className="p-2.5 sm:p-3 border rounded-md w-full pr-10 text-sm sm:text-base"
                                                            value={formData.dropZip}
                                                            onChange={(e) => handleDropZipChange(e.target.value)}
                                                        />
                                                        {isLookingUpDropPincode && (
                                                            <svg
                                                                className="animate-spin h-5 w-5 text-blue-500 absolute right-3 top-1/2 -translate-y-1/2"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                            >
                                                                <circle
                                                                    className="opacity-25"
                                                                    cx="12"
                                                                    cy="12"
                                                                    r="10"
                                                                    stroke="currentColor"
                                                                    strokeWidth="4"
                                                                />
                                                                <path
                                                                    className="opacity-75"
                                                                    fill="currentColor"
                                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                                />
                                                            </svg>
                                                        )}
                                                        {dropPincodeAutoFilled && !isLookingUpDropPincode && (
                                                            <p className="text-xs text-green-600 mt-1">
                                                                ✓ Auto-detected: {formData.dropCity},{" "}
                                                                {formData.dropState}
                                                            </p>
                                                        )}
                                                        {dropPincodeError && !isLookingUpDropPincode && (
                                                            <p className="text-xs text-orange-600 mt-1">
                                                                ⚠️ {dropPincodeError}
                                                            </p>
                                                        )}
                                                    </div>

                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-2">
                                                        <input
                                                            placeholder="Drop City"
                                                            className="p-2.5 sm:p-3 border rounded-md w-full text-sm sm:text-base"
                                                            value={formData.dropCity}
                                                            onChange={(e) =>
                                                                setFormData({ ...formData, dropCity: e.target.value })
                                                            }
                                                        />
                                                        <input
                                                            placeholder="Drop State"
                                                            className="p-2.5 sm:p-3 border rounded-md w-full text-sm sm:text-base"
                                                            value={formData.dropState}
                                                            onChange={(e) =>
                                                                setFormData({ ...formData, dropState: e.target.value })
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <UploadImage
                                            onFilesChange={setUploadedFiles}
                                            acceptedSlotImages={acceptedSlotImages}
                                            onAcceptedSlotImagesChange={setAcceptedSlotImages}
                                            selectedSubServices={subService}
                                        />

                                        <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 mt-6">
                                            <button
                                                type="button"
                                                onClick={() => setStep(1)}
                                                className="w-full sm:w-auto px-4 py-2.5 bg-gray-300 rounded-md hover:bg-gray-400 transition-colors text-sm sm:text-base font-medium"
                                            >
                                                ← Back
                                            </button>

                                            <button
                                                type="submit"
                                                disabled={!otpVerified || isSubmitting}
                                                className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white rounded-md disabled:bg-gray-300 hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base font-medium"
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <svg
                                                            className="animate-spin h-5 w-5 text-white"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                        >
                                                            <circle
                                                                className="opacity-25"
                                                                cx="12"
                                                                cy="12"
                                                                r="10"
                                                                stroke="currentColor"
                                                                strokeWidth="4"
                                                            />
                                                            <path
                                                                className="opacity-75"
                                                                fill="currentColor"
                                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                            />
                                                        </svg>
                                                        <span>Submitting...</span>
                                                    </>
                                                ) : (
                                                    <span>Submit Service Request</span>
                                                )}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </form>
                        </div>
                    );
                })()}

            {showErrorModal && apiError && <ErrorModal error={apiError} onClose={handleCloseErrorModal} />}
        </div>
    );
}